import { DocumentMapping, MARK_HIGH_PRIORITY_WORKFLOW } from "@/configuration/document-mappings/types";
import { PA_EAST_MAPPINGS } from "@/configuration/groups/pediatricassociates-east/document-mappings";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { Classification } from "@/src/models/constants/classifications";

/**
 * PA Central is the same customer's second eCW instance as PA East, running identical intake logic.
 * This file is a TRANSFORM over PA_EAST_MAPPINGS, not an independent source of truth: every entry is
 * East's entry with the tenant `group` restamped, plus a folder-name override for the 4 classifications
 * whose eCW folder name differs between the two instances (see FOLDER_OVERRIDES below). To change shared
 * intake behavior, edit pediatricassociates-east/document-mappings.ts — it applies here automatically.
 * If Central ever needs a real override beyond folder naming, extend toCentralMapping (or add another
 * per-classification override map alongside FOLDER_OVERRIDES) — don't fork this back into a duplicate.
 */

const FOLDER_OVERRIDES: Partial<Record<Classification, string>> = {
  [Classification.CONSENT_FOR_RECORDS_SPECIALIST]: "Referral Notes",
  [Classification.PRIOR_AUTHORIZATION_REFERRAL_RESPONSE]: "Referral Notes",
  [Classification.PROGRESS_NOTE]: "Referral Notes",
  [Classification.REFERRAL_RESPONSE]: "Referral Notes",
};

const STANDARD_DI_ADDITIONAL_WORKFLOW = PA_EAST_MAPPINGS[Classification.IMAGING_RESULTS_X_RAY]?.additionalWorkflow;

const ADDITIONAL_WORKFLOW_OVERRIDES: Partial<Record<Classification, readonly string[] | null>> = {
  [Classification.IMAGING_RESULTS_EKG]: STANDARD_DI_ADDITIONAL_WORKFLOW,
  [Classification.IMAGING_RESULTS_MRI]: STANDARD_DI_ADDITIONAL_WORKFLOW,
  [Classification.IMAGING_RESULTS_CT]: STANDARD_DI_ADDITIONAL_WORKFLOW,
  [Classification.IMAGING_RESULTS_ECHO]: STANDARD_DI_ADDITIONAL_WORKFLOW,
  [Classification.IMAGING_RESULTS_ULTRASOUND]: STANDARD_DI_ADDITIONAL_WORKFLOW,
  [Classification.IMAGING_RESULTS_STRESS_TEST]: STANDARD_DI_ADDITIONAL_WORKFLOW,
};

function toCentralMapping(classification: Classification, eastMapping: DocumentMapping): DocumentMapping {
  const folder = FOLDER_OVERRIDES[classification];
  const additionalWorkflow = ADDITIONAL_WORKFLOW_OVERRIDES[classification];

  return {
    ...eastMapping,
    group: ConfigSubdomain.PA_CENTRAL_REGION,
    ...(folder ? { folder, folderMapping: folder } : {}),
    ...(additionalWorkflow ? { additionalWorkflow } : {}),
  };
}

const LAB_ORDER_ATTACH_WORKFLOW = PA_EAST_MAPPINGS[Classification.LAB_RESULTS_OTHER]?.additionalWorkflow ?? [];

const PROCEDURE_ORDER_MANUAL_ATTACH_WORKFLOW = [
  "Automation matches the procedure order (shown in the Order Match panel) but does not attach the document. Attach it to the matched procedure order, select the Received checkbox, and fill the collected and received dates.",
  "If no procedure order matched, the document is assigned to the patient's PCP, unreviewed.",
];

const PROCEDURE_ORDER_ASSIGNEE_ONLY_WORKFLOW = [
  "Automation reads the ordering provider from the matched procedure order (shown in the Order Match panel) and assigns the document to them; it is not attached to the order.",
  "If no procedure order matched, the document is assigned to the patient's PCP, unreviewed.",
];

function screeningMapping(
  classification: Classification,
  fields: Pick<DocumentMapping, "additionalWorkflow" | "displayName" | "documentLabel" | "folder">
): DocumentMapping {
  return {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    assignedTo: "Ordering Clinician",
    folderMapping: fields.folder,
    inputDate: "",
    instructions: null,
    markAsReviewed: false,
    workflowAction: null,
    workflowType: "Orders WF",
    classification,
    group: ConfigSubdomain.PA_CENTRAL_REGION,
    ...fields,
  };
}

// Adult preventive-screening results only PA Central enables, so they have no East mapping to transform.
const PA_CENTRAL_SCREENING_RESULT_MAPPINGS: Partial<Record<Classification, DocumentMapping>> = {
  [Classification.IMAGING_RESULTS_COLONOSCOPY]: screeningMapping(Classification.IMAGING_RESULTS_COLONOSCOPY, {
    additionalWorkflow: [MARK_HIGH_PRIORITY_WORKFLOW, ...PROCEDURE_ORDER_ASSIGNEE_ONLY_WORKFLOW],
    displayName: "Colonoscopy Results",
    documentLabel: "{{ dateOfEvent YYYY/MM/DD }}, Colonoscopy Results",
    folder: "Procedure Documents",
  }),
  [Classification.IMAGING_RESULTS_MAMMOGRAM]: screeningMapping(Classification.IMAGING_RESULTS_MAMMOGRAM, {
    additionalWorkflow: [MARK_HIGH_PRIORITY_WORKFLOW, ...(STANDARD_DI_ADDITIONAL_WORKFLOW ?? [])],
    displayName: "Mammogram Results",
    documentLabel: "{{ dateOfEvent YYYY/MM/DD }}, Mammogram Results",
    folder: "Diagnostic Imaging Documents",
  }),
  [Classification.IMAGING_RESULTS_RETINAL]: screeningMapping(Classification.IMAGING_RESULTS_RETINAL, {
    additionalWorkflow: PROCEDURE_ORDER_ASSIGNEE_ONLY_WORKFLOW,
    displayName: "Diabetes Eye Examination Results",
    documentLabel: "{{ dateOfEvent YYYY/MM/DD }}, Diabetes Eye Examination Results",
    folder: "Procedure Documents",
  }),
  [Classification.LAB_RESULTS_COLOGUARD]: screeningMapping(Classification.LAB_RESULTS_COLOGUARD, {
    additionalWorkflow: [MARK_HIGH_PRIORITY_WORKFLOW, ...LAB_ORDER_ATTACH_WORKFLOW],
    displayName: "Cologuard Results",
    documentLabel: "{{ collectionDate YYYY/MM/DD }}, Cologuard Results",
    folder: "Lab Documents",
  }),
  [Classification.LAB_RESULTS_PAP_SMEAR]: screeningMapping(Classification.LAB_RESULTS_PAP_SMEAR, {
    additionalWorkflow: [MARK_HIGH_PRIORITY_WORKFLOW, ...PROCEDURE_ORDER_MANUAL_ATTACH_WORKFLOW],
    displayName: "Cervical Cancer Screening Results (PAP Smear)",
    documentLabel: "{{ collectionDate YYYY/MM/DD }}, Cervical Cancer Screening Results (PAP Smear)",
    folder: "Procedure Documents",
  }),
};

export const PA_CENTRAL_MAPPINGS: Partial<Record<Classification, DocumentMapping>> = (() => {
  const mappings: Partial<Record<Classification, DocumentMapping>> = { ...PA_CENTRAL_SCREENING_RESULT_MAPPINGS };

  for (const classification of Object.keys(PA_EAST_MAPPINGS) as Classification[]) {
    const eastMapping = PA_EAST_MAPPINGS[classification];

    if (eastMapping) mappings[classification] = toCentralMapping(classification, eastMapping);
  }

  return mappings;
})();
